/**
 * A program variant as a batch / slot: the server counts seats taken from
 * live deals and sends `seats` on every variant; these helpers turn that
 * into the badge, the tone and the "can this still be sold" answer that the
 * Programs page, the variant sheet and the lead's Attach program picker share.
 */
export type SeatStatus = "open" | "full" | "closed" | "unlimited";

export interface VariantSeats {
  capacity: number | null;
  /** Deals holding a seat: paid something, at a qualifying status, or at the last stage. */
  taken: number;
  enrolled: number;
  inProgress: number;
  /** Live deals that hold no seat yet (a link sent, nothing paid). Older servers omit it. */
  inTalks?: number;
  left: number | null;
  status: SeatStatus;
}

/** "3 in talks" — prospects on the batch who do not hold a seat; null when none. */
export function inTalksLabel(seats: VariantSeats | null | undefined): string | null {
  const n = seats?.inTalks ?? 0;
  return n > 0 ? `${n} in talks` : null;
}

export type SeatTone = "green" | "amber" | "red" | "grey";

export function isSellable(seats: VariantSeats | null | undefined): boolean {
  return !seats || seats.status === "open" || seats.status === "unlimited";
}

/** "8 of 20 seats left", "Full · 20 of 20 taken", "Closed for sales", "No seat limit"; null without seat data. */
export function seatsLabel(seats: VariantSeats | null | undefined): string | null {
  if (!seats) return null;
  switch (seats.status) {
    case "closed":
      return "Closed for sales";
    case "unlimited":
      return "No seat limit";
    case "full":
      return `Full · ${seats.taken} of ${seats.capacity} taken`;
    default:
      return `${seats.left} of ${seats.capacity} seats left`;
  }
}

/** Short form for tight rows: "8 left", "Full", "Closed", "Open". */
export function seatsShortLabel(seats: VariantSeats | null | undefined): string | null {
  if (!seats) return null;
  switch (seats.status) {
    case "closed":
      return "Closed";
    case "unlimited":
      return "Open";
    case "full":
      return "Full";
    default:
      return `${seats.left} left`;
  }
}

/** Amber once the last few seats are left (3 or fewer, or under a fifth of the batch). */
export function seatsTone(seats: VariantSeats | null | undefined): SeatTone {
  if (!seats) return "grey";
  if (seats.status === "closed") return "grey";
  if (seats.status === "full") return "red";
  if (seats.status === "unlimited") return "green";
  const left = seats.left ?? 0;
  const cap = seats.capacity ?? 0;
  return left <= 3 || (cap > 0 && left / cap < 0.2) ? "amber" : "green";
}

export const SEAT_TONE_CLASS: Record<SeatTone, string> = {
  green: "bg-[#dcfce7] text-[#156548]",
  amber: "bg-[#fef3c7] text-[#92400e]",
  red: "bg-[#fee2e2] text-[#991b1b]",
  grey: "bg-[#e5e7eb] text-[#52525b]",
};

const DAY = new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "short", year: "numeric" });

function day(v: unknown): string | null {
  if (!v) return null;
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? null : DAY.format(d);
}

/** "10 Nov 2026 – 20 Dec 2026", "From 10 Nov 2026", "Until 20 Dec 2026"; null without dates. */
export function formatBatchDates(startsOn: unknown, endsOn: unknown): string | null {
  const a = day(startsOn);
  const b = day(endsOn);
  if (a && b) return `${a} – ${b}`;
  if (a) return `From ${a}`;
  if (b) return `Until ${b}`;
  return null;
}

/** The variant's `startsOn` as the "YYYY-MM-DD" a date input edits. */
export function toDateInput(v: unknown): string {
  if (!v) return "";
  const d = new Date(String(v));
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/**
 * One line for a program card: seats left across its open batches, or that
 * every batch is full / closed. Null when no variant has a seat limit — the
 * card then says nothing new.
 */
export function programSeatsSummary(variants: ReadonlyArray<{ seats?: VariantSeats | null }>): string | null {
  const withSeats = variants.map((v) => v.seats).filter((s): s is VariantSeats => !!s);
  if (!withSeats.length) return null;
  const limited = withSeats.filter((s) => s.capacity != null || s.status === "closed");
  if (!limited.length) return null;
  const open = withSeats.filter((s) => s.status === "open");
  const unlimited = withSeats.filter((s) => s.status === "unlimited");
  if (open.length === 0 && unlimited.length === 0) {
    const closed = withSeats.every((s) => s.status === "closed");
    return closed ? "All batches closed" : "All batches full";
  }
  const left = open.reduce((n, s) => n + (s.left ?? 0), 0);
  const batches = open.length + unlimited.length;
  if (open.length === 0) return `${batches} open batch${batches === 1 ? "" : "es"}`;
  return `${left} seat${left === 1 ? "" : "s"} left · ${batches} open batch${batches === 1 ? "" : "es"}`;
}
